import { useState, useEffect, useCallback } from 'react';

type Theme = 'dark' | 'light' | 'system';
type ResolvedTheme = 'dark' | 'light';

/** @graph-connects none */
const STORAGE_KEY = 'cortex-admin-theme';

/** @graph-connects none */
function getSystemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/** @graph-connects none */
function resolveTheme(theme: Theme): ResolvedTheme {
  return theme === 'system' ? getSystemTheme() : theme;
}

/** @graph-connects none */
function applyTheme(resolved: ResolvedTheme) {
  document.documentElement.setAttribute('data-theme', resolved);
}

/**
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business テーマ管理フック。dark/light/systemの3モード対応、localStorage永続化、OS設定連動
 * @graph-connects none
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'dark' || stored === 'light' || stored === 'system' ? stored : 'dark';
  });

  const resolved = resolveTheme(theme);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    localStorage.setItem(STORAGE_KEY, next);
    applyTheme(resolveTheme(next));
  }, []);

  // OS設定変更の監視（systemモード時）
  useEffect(() => {
    applyTheme(resolved);

    if (theme !== 'system') return;

    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => {
      applyTheme(getSystemTheme());
    };
    mq.addEventListener('change', handler);
    return () => {
      mq.removeEventListener('change', handler);
    };
  }, [theme, resolved]);

  return { theme, resolved, setTheme } as const;
}
